import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { downscaleImage } from '../utils/imageDownscale';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../components/ui/dialog';
import {
  MessageCircle, ThumbsUp, Send, Pin, PinOff, Image as ImageIcon, X,
  ArrowLeft, Loader2, Megaphone, Shield, MapPin, Lock, Sparkles,
  Pencil, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import FeedCommentsPanel from '../components/FeedCommentsPanel';
import { FeedReactions } from '../components/FeedReactions';
import CommunityChannelTabs from '../components/CommunityChannelTabs';
import EventHighlightsFeed from '../components/EventHighlightsFeed';
import FeedPostContent, { stripMentionTokens } from '../components/FeedPostContent';

const isAdminRole = (role) => ['admin', 'super_admin', 'talent_admin', 'client_admin', 'finance_admin'].includes(role);

const CommunityFeed = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [posts, setPosts] = useState([]);
  // Iter-279 — full-screen reader for long post bodies + media lightbox.
  const [readerPost, setReaderPost] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [subscribed, setSubscribed] = useState(true);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pinNewPost, setPinNewPost] = useState(false);
  const [openComments, setOpenComments] = useState({}); // { [postId]: true }
  // 2026-06 — Edit / delete an existing post (author or admin, incl. system posts)
  const [editPost, setEditPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [activeChannel, setActiveChannel] = useState(searchParams.get('channel') || 'all');
  const [composerChannel, setComposerChannel] = useState('');
  // PO10 (Sprint 2026-08-05) — Community Feed Anywhere. Talent can tag a
  // post with an external professional event so their off-platform work
  // shows up in the community feed just like completed iEvntür shifts.
  const [showExternalEvent, setShowExternalEvent] = useState(false);
  const [externalEvent, setExternalEvent] = useState({
    event_title: '',
    brand_name: '',
    venue: '',
    event_date: '',
    event_type: 'brand_activation',
  });

  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setMe(res.data);
    } catch {}
  }, []);

  const fetchFeed = useCallback(async () => {
    // Iter-253c — Only show the full-page loader on the very first fetch.
    // Channel-tab switches and post-action refreshes keep the existing
    // posts visible to avoid the "white loading screen" between renders.
    setPosts((p) => {
      if (!p || p.length === 0) setLoading(true);
      return p;
    });
    try {
      const url = activeChannel && activeChannel !== 'all'
        ? `/feed?limit=50&channel=${activeChannel}`
        : '/feed?limit=50';
      const res = await api.get(url);
      setPosts(res.data.posts || res.data || []);
      if (typeof res.data.subscribed === 'boolean') setSubscribed(res.data.subscribed);
    } catch {
      toast.error('Failed to load community feed');
    } finally {
      setLoading(false);
    }
  }, [activeChannel]);

  useEffect(() => { fetchMe(); }, [fetchMe]);
  useEffect(() => { fetchFeed(); }, [fetchFeed]);

  // 2026-06 — mention / recognition notifications deep-link to ?post=<id>
  const focusPostId = searchParams.get('post');
  useEffect(() => {
    if (!focusPostId || loading) return;
    const el = document.querySelector(`[data-testid="feed-post-${focusPostId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusPostId, loading, posts]);

  const handleChannelChange = useCallback((slug) => {
    setActiveChannel(slug);
    if (slug === 'all') setSearchParams({});
    else setSearchParams({ channel: slug });
  }, [setSearchParams]);

  const handleImageSelect = async (file) => {
    if (!file) return;
    file = await downscaleImage(file); // mobile camera photos are 6-15MB raw
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      // Direct Cloudinary upload
      const sigRes = await api.get('/cloudinary/signature');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sigRes.data.api_key);
      formData.append('timestamp', sigRes.data.timestamp);
      formData.append('signature', sigRes.data.signature);
      if (sigRes.data.folder) formData.append('folder', sigRes.data.folder);

      const upRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sigRes.data.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      const upData = await upRes.json();
      if (!upRes.ok) throw new Error(upData.error?.message || 'Upload failed');
      setPostImage(upData.secure_url);
      setPostImagePreview(upData.secure_url);
    } catch (e) {
      toast.error(e.message || 'Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postContent.trim() && !postImage) {
      toast.error('Please enter some content or add an image');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        post_type: postImage ? 'event_recap' : 'text',
        content: postContent,
        media_urls: postImage ? [postImage] : [],
      };
      // Iter-251 — Channel selection
      const channelSlug = composerChannel || (activeChannel !== 'all' ? activeChannel : '');
      if (channelSlug) payload.channel_slug = channelSlug;
      // PO10 — attach external event metadata when user filled the form
      if (showExternalEvent && externalEvent.event_title.trim()) {
        payload.external_event = {
          event_title: externalEvent.event_title.trim(),
          brand_name:  externalEvent.brand_name.trim() || null,
          venue:       externalEvent.venue.trim() || null,
          event_date:  externalEvent.event_date || null,
          event_type:  externalEvent.event_type,
        };
      }
      const res = await api.post('/feed', payload);
      const newPostId = res.data.post_id;
      // Admins pinning on create
      if (pinNewPost && newPostId && isAdminRole(me?.role)) {
        try {
          await api.patch(`/feed/admin/${newPostId}/pin`, { pinned: true });
        } catch {}
      }
      toast.success(res.data.message || 'Post created');
      setPostContent('');
      setPostImage(null);
      setPostImagePreview(null);
      setPinNewPost(false);
      setComposerChannel('');
      setComposerOpen(false);
      setShowExternalEvent(false);
      setExternalEvent({ event_title: '', brand_name: '', venue: '', event_date: '', event_type: 'brand_activation' });
      fetchFeed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (postId) => {
    // Iter-253c — Optimistic update so the page doesn't flash a loading
    // spinner on every Like (the previous fetchFeed() call caused the
    // "white loading screen" Mel reported).
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              user_liked: !p.user_liked,
              likes_count: (p.likes_count || 0) + (p.user_liked ? -1 : 1),
            }
          : p,
      ),
    );
    try {
      await api.post(`/feed/${postId}/like`);
    } catch (e) {
      // Roll back on failure
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId
            ? {
                ...p,
                user_liked: !p.user_liked,
                likes_count: (p.likes_count || 0) + (p.user_liked ? -1 : 1),
              }
            : p,
        ),
      );
      toast.error('Could not update like');
    }
  };

  // Iter-253c — Reactions update handler (heart / happy / love emojis).
  // Receives the server's authoritative reactions block and merges it into
  // the local post list without refetching.
  const handleReactionUpdate = (postId, reactions) => {
    setPosts((prev) =>
      prev.map((p) => (p.id === postId ? { ...p, reactions } : p)),
    );
  };

  const handleTogglePin = async (postId, currentlyPinned) => {
    if (!isAdminRole(me?.role)) return;
    try {
      await api.patch(`/feed/admin/${postId}/pin`, { pinned: !currentlyPinned });
      toast.success(currentlyPinned ? 'Post unpinned' : 'Post pinned to top');
      fetchFeed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update pin');
    }
  };

  const homePath = me?.role === 'talent' ? '/talent/home' : me?.role === 'client' ? '/client/home' : '/admin/home';
  const canManagePost = (post) =>
    isAdminRole(me?.role) || (post.author_id && post.author_id === me?.id);

  const openEditor = (post) => {
    setEditPost(post);
    setEditContent(stripMentionTokens(post.content || ''));
  };

  const handleSaveEdit = async () => {
    if (!editPost) return;
    if (!editContent.trim()) {
      toast.error('Post content cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      const res = await api.patch(`/feed/${editPost.id}`, { content: editContent });
      setPosts((prev) => prev.map((p) => (
        p.id === editPost.id
          ? { ...p, content: res.data.content, mentions: res.data.mentions || [] }
          : p
      )));
      toast.success('Post updated');
      setEditPost(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update post');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePost = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/feed/${deleteTarget.id}`);
      setPosts((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success('Post deleted');
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  const canPost = isAdminRole(me?.role) || me?.role === 'talent' || me?.role === 'client';
  const canPin = isAdminRole(me?.role);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-cyan-50/30 to-white" data-testid="community-feed-page">
      <div className="max-w-3xl mx-auto px-3 sm:px-6 py-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(homePath)} data-testid="feed-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Home
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Megaphone className="w-6 h-6 text-cyan-500" /> Community Feed
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {isAdminRole(me?.role) ? 'Post announcements, pin important updates, engage with the community.' : 'See what\'s happening in the community.'}
              </p>
            </div>
          </div>
          {canPost && (
            <Button
              onClick={() => setComposerOpen(true)}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              size="sm"
              data-testid="feed-open-composer-btn"
            >
              <Send className="w-4 h-4 mr-1" /> New Post
            </Button>
          )}
        </div>

        {/* Iter-251 — Channel Tabs */}
        <div className="mb-4">
          <CommunityChannelTabs active={activeChannel} onChange={handleChannelChange} />
        </div>

        {/* Iter-313j — Event Highlights (replaces Creator Spotlights).
            Showcases real work from completed shifts. */}
        {activeChannel === 'all' && (
          <div className="mb-4">
            <EventHighlightsFeed />
          </div>
        )}

        {/* Feed */}
        <div className="space-y-4">
          {me?.role === 'client' && !subscribed && (
            <Card className="border-amber-300 bg-amber-50" data-testid="feed-upgrade-prompt">
              <CardContent className="py-4 flex items-start gap-3">
                <Lock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-900">Upgrade to see the full community feed</p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    You're seeing a limited preview. Active subscribers get full visibility into talent posts, recaps, and client tagging.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                  onClick={() => navigate('/clients/subscription')}
                  data-testid="feed-upgrade-btn"
                >
                  Upgrade
                </Button>
              </CardContent>
            </Card>
          )}
          {posts.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-gray-500 text-sm">
                No posts yet. Be the first to share something!
              </CardContent>
            </Card>
          )}
          {posts.map((post) => {
            const profileHref = post.author_role === 'talent'
              ? `/talent/profile/${post.author_id}`
              : post.author_role === 'client'
                ? `/clients/${post.author_id}`
                : null;
            const NameWrapper = profileHref ? Link : 'span';
            const nameProps = profileHref
              ? { to: profileHref, className: 'hover:underline' }
              : {};
            return (
            <Card key={post.id} className={`${post.is_pinned ? 'border-amber-300 bg-amber-50/30' : ''} ${focusPostId === post.id ? 'ring-2 ring-cyan-400' : ''}`} data-testid={`feed-post-${post.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {profileHref ? (
                      <Link
                        to={profileHref}
                        className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 hover:ring-2 hover:ring-cyan-400 transition"
                        data-testid={`feed-author-avatar-${post.id}`}
                      >
                        {post.author_photo ? (
                          <img src={post.author_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-gray-500 text-sm font-semibold">{(post.author_name || '?').charAt(0).toUpperCase()}</span>
                        )}
                      </Link>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {post.author_photo ? (
                          <img src={post.author_photo} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-gray-500 text-sm font-semibold">{(post.author_name || '?').charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <NameWrapper {...nameProps} data-testid={`feed-author-name-${post.id}`}>
                          <span className="text-sm font-semibold text-gray-900">{post.author_name || 'User'}</span>
                        </NameWrapper>
                        {isAdminRole(post.author_role) && (
                          <Badge variant="outline" className="text-[10px] border-cyan-300 text-cyan-700 flex items-center gap-1">
                            <Shield className="w-3 h-3" /> {post.author_role === 'super_admin' ? 'Super Admin' : 'Admin'}
                          </Badge>
                        )}
                        {post.is_pinned && (
                          <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-300">
                            <Pin className="w-3 h-3 mr-0.5" /> Pinned
                          </Badge>
                        )}
                        {post.channel_slug && (
                          <button
                            onClick={() => handleChannelChange(post.channel_slug)}
                            style={{ borderColor: post.channel_color || '#94a3b8', color: post.channel_color || '#475569' }}
                            className="text-[10px] px-1.5 py-0.5 rounded-full border bg-white/80 hover:bg-white font-semibold inline-flex items-center gap-1"
                            data-testid={`feed-post-channel-${post.id}`}
                            title={`View ${post.channel_name} channel`}
                          >
                            <span>{post.channel_icon || '•'}</span>
                            {post.channel_name}
                          </button>
                        )}
                      </div>
                      {(post.author_city || post.author_state) && (
                        <div
                          className="flex items-center gap-1 text-[11px] text-gray-600 mt-0.5"
                          data-testid={`feed-author-location-${post.id}`}
                        >
                          <MapPin className="w-3 h-3 text-gray-400" />
                          <span>{[post.author_city, post.author_state].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      <div
                        className="text-[11px] text-gray-500 mt-0.5"
                        data-testid={`feed-author-timestamp-${post.id}`}
                      >
                        {post.created_at ? new Date(post.created_at).toLocaleString() : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canPin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleTogglePin(post.id, post.is_pinned)}
                        className="h-8 px-2"
                        data-testid={`feed-pin-btn-${post.id}`}
                        title={post.is_pinned ? 'Unpin post' : 'Pin to top'}
                      >
                        {post.is_pinned ? <PinOff className="w-4 h-4 text-amber-600" /> : <Pin className="w-4 h-4 text-gray-400" />}
                      </Button>
                    )}
                    {canManagePost(post) && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditor(post)}
                          className="h-8 px-2"
                          data-testid={`feed-edit-btn-${post.id}`}
                          title="Edit post"
                        >
                          <Pencil className="w-4 h-4 text-gray-500" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteTarget(post)}
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`feed-delete-btn-${post.id}`}
                          title="Delete post"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {post.content && (() => {
                  const isLong = post.content.length > 280 || post.content.split('\n').length > 6;
                  return (
                    <div>
                      <p
                        className={`text-sm text-gray-800 whitespace-pre-wrap ${isLong ? 'line-clamp-6' : ''}`}
                        data-testid={`feed-post-body-${post.id}`}
                      >
                        <FeedPostContent content={post.content} />
                      </p>
                      {isLong && (
                        <button
                          type="button"
                          onClick={() => setReaderPost(post)}
                          className="text-xs text-cyan-600 hover:text-cyan-700 font-semibold mt-1"
                          data-testid={`feed-post-read-more-${post.id}`}
                        >
                          Read more →
                        </button>
                      )}
                    </div>
                  );
                })()}
                {Array.isArray(post.media_urls) && post.media_urls.length > 0 && (() => {
                  const url = post.media_urls[0];
                  const isVideo = /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url);
                  return isVideo ? (
                    <video
                      src={url}
                      controls
                      playsInline
                      className="w-full max-h-[480px] rounded-md bg-black cursor-zoom-in"
                      onClick={() => setLightboxUrl(url)}
                      data-testid={`feed-post-video-${post.id}`}
                    />
                  ) : (
                    <img
                      src={url}
                      alt=""
                      className="w-full max-h-[480px] object-cover rounded-md cursor-zoom-in"
                      onClick={() => setLightboxUrl(url)}
                      data-testid={`feed-post-image-${post.id}`}
                    />
                  );
                })()}
                {/* PO10 — External event badge shown when the post was
                    tagged with an off-platform professional event. */}
                {post.external_event && post.external_event.event_title && (
                  <div
                    className="mt-2 flex items-start gap-2 p-2 rounded-md bg-cyan-50 border border-cyan-200"
                    data-testid={`external-event-${post.id}`}
                  >
                    <Sparkles className="w-3.5 h-3.5 text-cyan-700 mt-0.5 shrink-0" />
                    <div className="text-xs text-cyan-900">
                      <div className="font-semibold">{post.external_event.event_title}</div>
                      <div className="text-cyan-700 flex flex-wrap gap-x-2">
                        {post.external_event.brand_name && <span>{post.external_event.brand_name}</span>}
                        {post.external_event.venue && <span>· {post.external_event.venue}</span>}
                        {post.external_event.event_date && (
                          <span>· {new Date(post.external_event.event_date).toLocaleDateString()}</span>
                        )}
                        {post.external_event.event_type && (
                          <span className="uppercase tracking-wide text-[10px] bg-white border border-cyan-200 rounded px-1 py-0.5 ml-1">
                            {String(post.external_event.event_type).replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 pt-1 border-t border-gray-100 flex-wrap">
                  {/* Iter-296e — Removed the legacy ThumbsUp button. The
                      `<FeedReactions>` bar below already renders the
                      like emoji + total count + the full emoji picker
                      (Heart, Laugh, Wow, Sad, Angry). Having both
                      caused a duplicate "like" row on every post. */}
                  <FeedReactions
                    postId={post.id}
                    reactions={post.reactions}
                    onUpdate={handleReactionUpdate}
                  />
                  <button
                    onClick={() => setOpenComments((o) => ({ ...o, [post.id]: !o[post.id] }))}
                    className={`flex items-center gap-1.5 text-xs transition-colors ml-auto ${openComments[post.id] ? 'text-cyan-600' : 'text-gray-500 hover:text-cyan-600'}`}
                    data-testid={`feed-toggle-comments-btn-${post.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> {post.comments_count || 0}
                  </button>
                </div>
                {openComments[post.id] && (
                  <FeedCommentsPanel
                    postId={post.id}
                    onCountChange={() => {
                      // Increment client-side for immediate feedback
                      setPosts((ps) => ps.map((p) => p.id === post.id ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
                    }}
                  />
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>
      </div>

      {/* Composer Dialog */}
      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isAdminRole(me?.role) ? 'Post an announcement' : 'Share with the community'}
            </DialogTitle>
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="What's happening?"
              rows={5}
              data-testid="feed-composer-content"
            />
            {postImagePreview && (
              <div className="relative">
                <img src={postImagePreview} alt="" className="w-full max-h-[240px] object-cover rounded-md" />
                <button
                  onClick={() => { setPostImage(null); setPostImagePreview(null); }}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center"
                  data-testid="feed-composer-remove-img"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {/* Iter-251 — Channel selector */}
            <ChannelSelector
              value={composerChannel || (activeChannel !== 'all' ? activeChannel : '')}
              onChange={setComposerChannel}
            />
            {/* PO10 — External event tagging (Community Feed Anywhere) */}
            <div className="border border-gray-200 rounded-md">
              <button
                type="button"
                onClick={() => setShowExternalEvent(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-cyan-700 hover:bg-cyan-50"
                data-testid="feed-composer-external-event-toggle"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  {showExternalEvent ? 'Hide' : 'Add'} external event details
                </span>
                <span className="text-[10px] text-gray-500 font-normal">
                  {showExternalEvent ? '−' : '+'} Optional — for off-platform events
                </span>
              </button>
              {showExternalEvent && (
                <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="text"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Event name (e.g. Sephora Store Launch)"
                      value={externalEvent.event_title}
                      onChange={(e) => setExternalEvent({ ...externalEvent, event_title: e.target.value })}
                      data-testid="external-event-title"
                    />
                    <input
                      type="text"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Brand / Client"
                      value={externalEvent.brand_name}
                      onChange={(e) => setExternalEvent({ ...externalEvent, brand_name: e.target.value })}
                      data-testid="external-event-brand"
                    />
                    <input
                      type="text"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      placeholder="Venue / City"
                      value={externalEvent.venue}
                      onChange={(e) => setExternalEvent({ ...externalEvent, venue: e.target.value })}
                      data-testid="external-event-venue"
                    />
                    <input
                      type="date"
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                      value={externalEvent.event_date}
                      onChange={(e) => setExternalEvent({ ...externalEvent, event_date: e.target.value })}
                      data-testid="external-event-date"
                    />
                  </div>
                  <select
                    className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                    value={externalEvent.event_type}
                    onChange={(e) => setExternalEvent({ ...externalEvent, event_type: e.target.value })}
                    data-testid="external-event-type"
                  >
                    <option value="brand_activation">Brand Activation</option>
                    <option value="trade_show">Trade Show / Expo</option>
                    <option value="private_event">Private Event</option>
                    <option value="corporate">Corporate Event</option>
                    <option value="other">Other</option>
                  </select>
                  <p className="text-[10px] text-gray-500 italic">
                    Use this when you&apos;re posting about a professional event you worked outside of iEvntür.
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-cyan-600">
                <ImageIcon className="w-4 h-4" />
                {uploading ? 'Uploading…' : postImage ? 'Change image' : 'Add image'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageSelect(e.target.files?.[0])}
                  data-testid="feed-composer-image-input"
                />
              </label>
              {isAdminRole(me?.role) && (
                <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pinNewPost}
                    onChange={(e) => setPinNewPost(e.target.checked)}
                    data-testid="feed-composer-pin-toggle"
                  />
                  <Pin className="w-3.5 h-3.5 text-amber-600" /> Pin to top after posting
                </label>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setComposerOpen(false)} size="sm" data-testid="feed-composer-cancel">Cancel</Button>
            <Button
              onClick={handleCreatePost}
              disabled={creating || (!postContent.trim() && !postImage)}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              size="sm"
              data-testid="feed-composer-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Post
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-06 — Edit an existing post (updates in place, never duplicates) */}
      <Dialog open={!!editPost} onOpenChange={(o) => !o && setEditPost(null)}>
        <DialogContent data-testid="feed-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit post</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Talent names are automatically turned into @mentions when you save.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            rows={7}
            data-testid="feed-edit-content"
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditPost(null)} data-testid="feed-edit-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveEdit}
              disabled={savingEdit || !editContent.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="feed-edit-save"
            >
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-06 — Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="feed-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this post?</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              This permanently removes the post along with its comments, likes and
              reactions. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} data-testid="feed-delete-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleDeletePost}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="feed-delete-confirm"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Delete permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter-279 — Full-screen reader for long post bodies */}
      <Dialog open={!!readerPost} onOpenChange={(o) => !o && setReaderPost(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="feed-post-reader">
          <DialogHeader>
            <DialogTitle className="text-base">
              {readerPost?.author_name || 'Post'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {readerPost?.created_at ? new Date(readerPost.created_at).toLocaleString() : ''}
            </DialogDescription>
          </DialogHeader>
          {readerPost && (
            <div className="space-y-3">
              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="feed-post-reader-body">
                <FeedPostContent content={readerPost.content} />
              </p>
              {Array.isArray(readerPost.media_urls) && readerPost.media_urls[0] && (
                /\.(mp4|mov|webm|m4v)(\?|$)/i.test(readerPost.media_urls[0]) ? (
                  <video src={readerPost.media_urls[0]} controls className="w-full rounded-md bg-black" />
                ) : (
                  <img
                    src={readerPost.media_urls[0]}
                    alt=""
                    className="w-full rounded-md cursor-zoom-in"
                    onClick={() => setLightboxUrl(readerPost.media_urls[0])}
                  />
                )
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter-279 — Full-screen media lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
          data-testid="feed-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            aria-label="Close lightbox"
            data-testid="feed-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          {/\.(mp4|mov|webm|m4v)(\?|$)/i.test(lightboxUrl) ? (
            <video
              src={lightboxUrl}
              controls
              autoPlay
              playsInline
              className="max-h-[95vh] max-w-full rounded"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={lightboxUrl}
              alt=""
              className="max-h-[95vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CommunityFeed;

// Iter-251 — Channel selector for the post composer
const ChannelSelector = ({ value, onChange }) => {
  const [channels, setChannels] = useState([]);
  useEffect(() => {
    api.get('/community/channels')
      .then((r) => setChannels(r.data.channels || []))
      .catch(() => {});
  }, []);
  return (
    <div data-testid="composer-channel-selector">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
        Post to channel
      </div>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onChange('')}
          className={`text-[11px] px-2.5 py-1 rounded-full border ${
            !value ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
          }`}
          data-testid="composer-channel-none"
        >
          General
        </button>
        {channels.map((c) => (
          <button
            key={c.slug}
            type="button"
            onClick={() => onChange(c.slug)}
            style={value === c.slug ? { backgroundColor: c.color, borderColor: c.color, color: 'white' } : {}}
            className={`text-[11px] px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${
              value === c.slug ? 'shadow' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
            }`}
            data-testid={`composer-channel-${c.slug}`}
          >
            <span>{c.icon}</span> {c.name}
          </button>
        ))}
      </div>
    </div>
  );
};
